import { ItemUpdateConflictReason } from '@mimir/common/dto/itemModelPrimitives';
import type { ItemMetaData } from '@mimir/common/dto/fieldValue';
import type { FieldValueDiff, MdfDiffStrict, MetadataDeltaPayload } from '@mimir/common/dto/mdfModels';
import { getMdfDiffStrict } from '@mimir/common/mdf/MetaDataForm';
import { getErrorDetails } from '@mimir/common/util/exception';
import type { HttpQueryParameters, AdditionalParams } from '@mimir/common/util/apiClient';
import { assert } from '@mimir/common/util/invariant';
import isEqual from 'lodash/isEqual';
import type { Logger } from '@mimir/common/types/logger';

/**
 * The ItemMetadataUpdater does partial and conditional updates
 * to Item metadata.
 *
 * If a conditional update fails it will try to automatically
 * reapply the change on the latest server state, and if unable
 * to do so will return conflict info to the caller.
 */
export class ItemMetadataUpdater {

  constructor(
    private readonly api: MetadataUpdateApi,
    private readonly logger: Logger,
  ) {
  }

  async tryUpdateMetadata(
    itemId: string,
    update: UpdateMetadataParams,
  ): Promise<TryUpdateMetadataResult> {
    const editDiff = getMdfDiffStrict(update.editBase.metadata, update.editedMetadata);
    return this.conditionalUpdate(itemId, update.editBase, editDiff, 0);
  }

  private async conditionalUpdate(
    itemId: string,
    editBase: MetadataState,
    editDiff: MdfDiffStrict,
    reapplyCount: number,
  ): Promise<TryUpdateMetadataResult> {
    if (reapplyCount > 10) {
      // Such a high retry count would likely indicate a code bug
      // instead of an actual conflict, so to be safe we bail out.
      throw Error(`Gave up updating metadata after ${reapplyCount} attempts`);
    }
    const queryParams: HttpQueryParameters = {
      requiredMetadataVersion: editBase.metadataVersion,
    };
    const conflictInfo = checkConflicts(editBase, editDiff);
    if (conflictInfo) {
      this.logger.info('Metadata conflict: ' + conflictInfo.status);
      // If there are conflicts we need the user to resolve those
      return conflictInfo;
    }
    const metadataDelta = diffToUpdateDto(editDiff);
    try {
      const updatedItem = await this.api.updateMetadata(
        itemId,
        { metadataDelta },
        { queryParams },
      );
      const metadata = updatedItem.metadata;
      const metadataVersion = updatedItem.metadataVersion;
      assert(metadata, 'conditionalUpdate: Expecting metadata for updated item');
      assert(metadataVersion, 'conditionalUpdate: Expecting metadataVersion for updated item');
      return {
        status: 'updated',
        latest: { metadata, metadataVersion }
      };
    } catch (err) {
      if (isPreConditionFailure(err)) {
        this.logger.info('Metadata update conflict, will try to reapply change');
        return await this.tryReapply(this.logger, itemId, editDiff, reapplyCount);
      }
      throw err;
    }
  }

  private async tryReapply(
    logger: Logger,
    itemId: string,
    editDiff: MdfDiffStrict,
    reapplyCount: number,
  ): Promise<TryUpdateMetadataResult> {
    const serverState = await this.api.getMetadata(itemId);
    logger.debug('Reapply against ' + serverState.metadataVersion);
    return this.conditionalUpdate(itemId, serverState, editDiff, reapplyCount + 1);
  }
}

function checkConflicts(
  serverState: MetadataState,
  editDiff: MdfDiffStrict,
): MetadataConflict | null {
  const serverMetadata = serverState.metadata;
  if (serverMetadata.formId !== editDiff.oldMdfId && serverMetadata.formId !== editDiff.newMdfId) {
    // The server and our diff has conflicting formId change
    return {
      status: 'schema_conflict',
      conflictMetadata: serverState,
    };
  }

  const serverFields = serverMetadata.formData;
  const conflictingFields: FieldValueDiff[] = [];
  for (const fieldDiff of editDiff.metadataFields) {
    if (fieldDiff.newValue === undefined) {
      continue;
    }
    const serverValue = serverFields[fieldDiff.fieldId];
    if (!isEqual(fieldDiff.oldValue, serverValue) && !isEqual(fieldDiff.newValue, serverValue)) {
      // We've changed a field that has changed to a different value on the server
      conflictingFields.push({
        fieldId: fieldDiff.fieldId,
        oldValue: serverValue,
        newValue: fieldDiff.newValue,
      });
    }
  }

  if (conflictingFields.length === 0) {
    return null;
  }
  return {
    status: 'fields_conflict',
    conflictingFields,
    conflictMetadata: serverState,
  };
}

function diffToUpdateDto(editDiff: MdfDiffStrict): MetadataDeltaPayload {
  const update: MetadataDeltaPayload = {
    formId: editDiff.newMdfId,
    formData: {},
  };

  for (const fieldDiff of editDiff.metadataFields) {
    if (fieldDiff.newValue !== undefined) {
      // If there is a defined newValue, we use it.
      update.formData[fieldDiff.fieldId] = fieldDiff.newValue;
    } else if (fieldDiff.oldValue !== undefined) {
      // Went from defined to undefined, so we clear it
      update.formData[fieldDiff.fieldId] = null;
    }
  }

  return update;
}

function isPreConditionFailure(err: any) {
  const errDetails = getErrorDetails(err);
  const reason = errDetails.reason ?? err.reason;
  return errDetails.code?.toString() === '409' &&
    reason === ItemUpdateConflictReason.PreconditionFailed;
}

export type MetadataUpdateApi = {

  updateMetadata: (
    itemId: string,
    payload: { metadataDelta: MetadataDeltaPayload },
    options?: AdditionalParams
  ) => Promise<MetadataState>;

  getMetadata: (itemId: string) => Promise<MetadataState>;
};

export type TryUpdateMetadataResult =
  {
    status: 'updated',
    // The full metadata returned from the server, after
    // applying the user's update.
    latest: MetadataState
  } | MetadataConflict;

export type UpdateMetadataParams = {
  // The metadata state the user edits are based on,
  // which should be the last server state the client
  // saw before initiating the save.
  editBase: MetadataState,

  // The metadata state the user wants to save
  editedMetadata: ItemMetaData,
};

export type MetadataState = {
  metadata: ItemMetaData,
  metadataVersion: number,
};

type MetadataConflict = {
  // The metadata that was in conflict with our changes.
  conflictMetadata: MetadataState,
} & (SchemaConflict | FieldsConflict);

type FieldsConflict = {
  status: 'fields_conflict',

  // The fields that had conflicting updates
  conflictingFields: FieldValueDiff[];

};

type SchemaConflict = {
  status: 'schema_conflict',
};
